<template>
  <dialog-layout
    title="Edit team"
    :fullscreen="fullscreen"
    :navIcon="navIcon"
    :dark="dark"
    @pop-dialog="$emit('pop-dialog')"
  >
    <v-container v-if="squad">
      <v-text-field
        ref="nameInput"
        v-model="localName"
        label="Team name"
        variant="underlined"
        autocomplete="off"
        spellcheck="false"
        class="mb-2"
        @keydown.enter="onEnter"
      />

      <v-select
        v-model="localLocationId"
        label="Home location"
        variant="outlined"
        density="compact"
        autocomplete="off"
        spellcheck="false"
        class="mb-2 mt-6"
        :items="squad.league.locations"
        item-title="name"
        item-value="id"
        :disabled="(squad.league.locations.length === 0)"
      >
        <template v-if="squad.league.locations.length === 0" v-slot:prepend-item>
          <v-list-subheader>
          No locations yet.
          </v-list-subheader>
        </template>
      </v-select>

      <!-- Members are frozen mid-race: a new member would take part in the rating replay with
           startOfRaceMu/Sigma still at their 0 default, so the next scored game would write a
           near-zero rating over their real scorecard. Ratings therefore can't be trusted for
           anyone added after the race's first game is scored but before it is decided. -->
      <p v-if="membersEditingBlocked" class="text-body-2 text-error mb-2">
        Members can't be changed while this team is mid-race (games scored, race not decided).
      </p>
      <p v-else class="text-body-2 text-medium-emphasis mb-2 mt-4">
        Team players - 3 players per team
      </p>

      <v-list v-if="availableMembers.length > 0">
        <v-list-item
          v-for="member in availableMembers"
          :key="member.id"
          class="px-0"
        >
          <v-checkbox
            v-model="localMemberIds"
            :value="member.id"
            density="compact"
            hide-details
            :disabled="membersEditingBlocked || (localMemberIds.length >= 3 && !localMemberIds.includes(member.id))"
          >
            <template v-slot:label>
              {{ `${member.name} (${member.startOfLeagueDisplayRating})` }}
            </template>
          </v-checkbox>
        </v-list-item>
      </v-list>
      <p v-else class="text-body-2 text-medium-emphasis">
        No players available.
      </p>

      <div class="d-flex justify-end mt-16">
        <v-btn class="mr-2" @click="$emit('pop-dialog')">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="saving || !localName.trim() || !hasChanges"
          @click="save"
        >
          Save
        </v-btn>
      </div>
    </v-container>
  </dialog-layout>
</template>

<script>
import { DialogLayout } from "vue-app-components";
import { squadHasInProgressRace } from "@/utils/race-utils";

export default {
  name: "dialog-squads",
  components: {
    DialogLayout
  },
  props: {
    fullscreen: {
      type: Boolean,
      required: true
    },
    navIcon: {
      type: String,
      required: true
    },
    dark: {
      type: Boolean,
      default: false
    }
  },
  inject: ["$store"],
  data () {
    return {
      localLocationId: "",
      localMemberIds: [],
      localName: "",
      saving: false,
      squad: null
    };
  },
  created () {
    const id = this.$dialog.getId();
    if (!id) return;
    const handler = (squads) => {
      if (!squads) return;
      const s = squads.find(s => s.id === id);
      if (s) {
        this.squad = s;
        this.localName = s.name === "Untitled" ? "" : s.name;
        if (!this.localName) {
          this.$nextTick(() => this.$refs?.nameInput?.focus());
        }
        this.localLocationId = s.locationId || "";
        this.localMemberIds = [...s.memberIds];
        unwatch();
      }
    };
    const unwatch = this.$watch(() => this.$store.league?.squads, handler);
    handler(this.$store.league?.squads);
  },
  computed: {
    availableMembers () {
      if (!this.squad) return [];
      const currentSquadId = this.squad.id;
      const assignedIds = new Set(
        this.squad.league.squads
          .filter(squad => squad.id !== currentSquadId)
          .reduce((ids, squad) => ids.concat(squad.memberIds || []), [])
      );
      return this.squad.league.members.filter(member => !assignedIds.has(member.id));
    },
    hasChanges () {
      if (!this.squad) return false;
      const nameChanged = this.localName.trim() !== (this.squad.name === "Untitled" ? "" : this.squad.name);
      const locationChanged = this.localLocationId !== (this.squad.locationId || "");
      const membersChanged = JSON.stringify([...this.localMemberIds].sort()) !== JSON.stringify([...this.squad.memberIds].sort());
      return nameChanged || locationChanged || membersChanged;
    },
    membersEditingBlocked () {
      return !!this.squad && squadHasInProgressRace(this.squad.league, this.squad);
    }
  },
  methods: {
    onEnter () {
      if (this.saving || !this.localName.trim() || !this.hasChanges) return;
      this.save();
    },
    save () {
      if (this.saving || !this.squad) return;
      this.saving = true;
      this.squad.immediateUpdate({
        name: this.localName.trim(),
        locationId: this.localLocationId,
        memberIds: this.localMemberIds.slice()
      }).then(() => {
        this.$emit("pop-dialog");
      }).finally(() => {
        this.saving = false;
      });
    }
  }
};
</script>